import { Fragment, useEffect, useMemo, useState } from "react";
import {
 Badge,
 Button,
 Card,
 Col,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
 Cell,
 Legend,
 Pie,
 PieChart,
 ResponsiveContainer,
 Tooltip,
} from "recharts";
import api from "../../../api";
import usePermission from "../../../hooks/usePermission";
import PageTitle from "../../layouts/PageTitle";

const statCards = [
 {
 key: "warehouses",
 label: "Warehouses",
 icon: "fa-warehouse",
 path: "/warehouses",
 tone: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
 },
 {
 key: "purchaseOrders",
 label: "Inventory POs",
 icon: "fa-file-invoice",
 path: "/inventory-purchase-orders",
 tone: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
 },
 {
 key: "stockItems",
 label: "Stock Items",
 icon: "fa-boxes-stacked",
 path: "/inventory-stock",
 tone: "linear-gradient(135deg, #b45309 0%, #f59e0b 100%)",
 },
 {
 key: "receivedPOs",
 label: "Received POs",
 icon: "fa-truck-ramp-box",
 path: "/inventory-purchase-orders?status=Received",
 tone: "linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)",
 },
];

const COLORS = ["#0f766e", "#1d4ed8", "#b45309", "#7c3aed", "#059669", "#dc2626"];
const cardAccents = {
 warehouses: { color: "#0f766e", bg: "rgba(15, 118, 110, 0.08)", iconBg: "rgba(15, 118, 110, 0.12)" },
 purchaseOrders: { color: "#1d4ed8", bg: "rgba(29, 78, 216, 0.08)", iconBg: "rgba(29, 78, 216, 0.12)" },
 stockItems: { color: "#b45309", bg: "rgba(180, 83, 9, 0.08)", iconBg: "rgba(180, 83, 9, 0.12)" },
 receivedPOs: { color: "#6d28d9", bg: "rgba(109, 40, 217, 0.08)", iconBg: "rgba(109, 40, 217, 0.12)" },
};

const StatCard = ({ card, value, loading, onClick }) => {
 const accent = cardAccents[card.key];

 return (
 <Card
 className="border-0 shadow-sm h-100 overflow-hidden"
 role="button"
 onClick={onClick}
 style={{ cursor: "pointer", borderLeft: `4px solid ${accent.color}`, borderRadius: 12 }}
 >
 <Card.Body className="p-3 p-xl-4">
 <div className="d-flex justify-content-between align-items-start gap-3">
 <div className="overflow-hidden">
 <p className="mb-1 small text-uppercase text-muted fw-semibold">{card.label}</p>
 <h3 className="mb-0 fw-bold" style={{ color: accent.color }}>
 {loading ? <Spinner size="sm" animation="border" /> : value}
 </h3>
 </div>
 <div
 className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
 style={{
 width: 48,
 height: 48,
 background: accent.iconBg,
 color: accent.color,
 }}
 >
 <i className={`fas ${card.icon} fs-5`} />
 </div>
 </div>
 </Card.Body>
 </Card>
 );
};

const SectionHeader = ({ title, subtitle, action }) => (
 <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
 <div>
 <h6 className="fw-bold mb-1">
 <i className="fas fa-chart-line text-primary me-2" />
 {title}
 </h6>
 {subtitle && <div className="text-muted small">{subtitle}</div>}
 </div>
 {action}
 </div>
);

const InventoryDashboard = () => {
 const navigate = useNavigate();
 const { hasPermission } = usePermission();
 const [loading, setLoading] = useState(true);
 const [stats, setStats] = useState({
 warehouses: 0,
 purchaseOrders: 0,
 stockItems: 0,
 receivedPOs: 0,
 });
 const [stockRows, setStockRows] = useState([]);
 const [poRows, setPoRows] = useState([]);

 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);

 const [warehouseRes, poRes, stockRes, receivedRes] = await Promise.all([
 api.get("/warehouses?limit=1"),
 api.get("/inventory-purchase-orders?limit=500"),
 api.get("/inventory-purchase-orders/stock/summary"),
 api.get("/inventory-purchase-orders?status=Received&limit=1"),
 ]);

 const poList = poRes.data?.data || [];

 setStats({
 warehouses: warehouseRes.data?.pagination?.totalCount || 0,
 purchaseOrders: poRes.data?.pagination?.totalCount || poList.length || 0,
 stockItems: stockRes.data?.data?.length || 0,
 receivedPOs: receivedRes.data?.pagination?.totalCount || 0,
 });
 setStockRows(stockRes.data?.data || []);
 setPoRows(poList);
 } catch (error) {
 // Keep page usable even if counts fail
 setStats({
 warehouses: 0,
 purchaseOrders: 0,
 stockItems: 0,
 receivedPOs: 0,
 });
 setStockRows([]);
 setPoRows([]);
 } finally {
 setLoading(false);
 }
 };

 load();
 }, []);

 const visibleCards = useMemo(
 () => statCards.filter((card) => hasPermission("read:inventory")),
 [hasPermission]
 );

 const warehouseChartData = useMemo(() => {
 const map = new Map();
 stockRows.forEach((row) => {
 const key = row.warehouse?.warehouseName || "Unknown";
 map.set(key, (map.get(key) || 0) + Number(row.quantityOnHand || 0));
 });
 return [...map.entries()].map(([name, value]) => ({ name, value }));
 }, [stockRows]);

 const warehousePieData = useMemo(
 () => warehouseChartData.slice(0, 8),
 [warehouseChartData]
 );

 const topItemsData = useMemo(() => {
 return [...stockRows]
 .sort((a, b) => Number(b.quantityOnHand || 0) - Number(a.quantityOnHand || 0))
 .slice(0, 8)
 .map((row) => ({
 name: row.itemName || "-",
 qty: Number(row.quantityOnHand || 0),
 }));
 }, [stockRows]);

 const poStatusData = useMemo(() => {
 const map = new Map();
 poRows.forEach((po) => {
 map.set(po.status || "Unknown", (map.get(po.status || "Unknown") || 0) + 1);
 });
 return [...map.entries()].map(([name, value]) => ({ name, value }));
 }, [poRows]);

 const lowStockItems = useMemo(() => {
 return [...stockRows]
 .filter((row) => Number(row.quantityOnHand || 0) <= 10)
 .sort((a, b) => Number(a.quantityOnHand || 0) - Number(b.quantityOnHand || 0))
 .slice(0, 5);
 }, [stockRows]);

 const totalOnHand = useMemo(
 () => stockRows.reduce((sum, row) => sum + Number(row.quantityOnHand || 0), 0),
 [stockRows]
 );

 const warehouseCountWithStock = useMemo(
 () => new Set(stockRows.map((row) => row.warehouse?._id || row.warehouse?.warehouseName || "unknown")).size,
 [stockRows]
 );

 const lowStockCount = useMemo(
 () => stockRows.filter((row) => Number(row.quantityOnHand || 0) <= 10).length,
 [stockRows]
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Inventory Dashboard" motherMenu="Inventory" />
 <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
 <div>
 <h4 className="mb-1 fw-bold">Inventory Dashboard</h4>
 <div className="text-muted small">Overview of inventory stock, warehouses, and purchase orders.</div>
 </div>
 <div className="d-flex gap-2 flex-wrap">
 <Button as={Link} to="/inventory-purchase-orders/add" variant="primary">
 Add Inventory PO
 </Button>
 <Button as={Link} to="/inventory-purchase-orders" variant="outline-secondary">
 Purchase Orders
 </Button>
 <Button as={Link} to="/inventory-stock" variant="outline-primary">
 View summary
 </Button>
 </div>
 </div>

 <Row className="g-4">

 {visibleCards.map((card) => (
 <Col key={card.key} xl={3} lg={6} md={6}>
 <StatCard
 card={card}
 value={stats[card.key]}
 loading={loading}
 onClick={() => navigate(card.path)}
 />
 </Col>
 ))}

 <Col xl={12}>
 <Row className="g-3">
 <Col md={4}>
 <Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #0f766e" }}>
 <Card.Body className="p-3 p-xl-4">
 <div className="text-muted small text-uppercase fw-semibold mb-1">Total Qty On Hand</div>
 <h4 className="fw-bold mb-1 text-dark">{loading ? <Spinner size="sm" animation="border" /> : totalOnHand.toLocaleString("en-IN")}</h4>
 <div className="text-muted small">Across {warehouseCountWithStock} warehouses</div>
 </Card.Body>
 </Card>
 </Col>
 <Col md={4}>
 <Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #b45309" }}>
 <Card.Body className="p-3 p-xl-4">
 <div className="text-muted small text-uppercase fw-semibold mb-1">Low Stock Items</div>
 <h4 className="fw-bold mb-1 text-dark">{loading ? <Spinner size="sm" animation="border" /> : lowStockCount}</h4>
 <div className="text-muted small">Items at or below threshold</div>
 </Card.Body>
 </Card>
 </Col>
 <Col md={4}>
 <Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #6d28d9" }}>
 <Card.Body className="p-3 p-xl-4">
 <div className="text-muted small text-uppercase fw-semibold mb-1">PO Status Mix</div>
 <h4 className="fw-bold mb-1 text-dark">{loading ? <Spinner size="sm" animation="border" /> : poStatusData.length}</h4>
 <div className="text-muted small">Active status categories in dashboard</div>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Col>

 <Col xl={8} lg={12}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body className="p-4">
 <SectionHeader
 title="Stock by Warehouse"
 subtitle="Quantity on hand distributed across warehouses."
 action={
 <Button as={Link} to="/inventory-stock" variant="outline-primary" size="sm">
 View Summary
 </Button>
 }
 />
 {warehousePieData.length ? (
 <ResponsiveContainer width="100%" height={320}>
 <PieChart>
 <Pie
 data={warehousePieData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={110}
 innerRadius={60}
 paddingAngle={4}
 label
 >
 {warehousePieData.map((entry, index) => (
 <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 ) : (
 <div className="text-center text-muted py-5">No stock data available</div>
 )}
 </Card.Body>
 </Card>
 </Col>

 <Col xl={4} lg={12}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body className="p-4">
 <SectionHeader
 title="Purchase Order Status"
 subtitle="Current PO pipeline by status."
 />
 {poStatusData.length ? (
 <ResponsiveContainer width="100%" height={280}>
 <PieChart>
 <Pie
 data={poStatusData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={96}
 innerRadius={56}
 paddingAngle={3}
 label
 >
 {poStatusData.map((entry, index) => (
 <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 ) : (
 <div className="text-center text-muted py-5">No PO data available</div>
 )}
 </Card.Body>
 </Card>
 </Col>

 <Col xl={8} lg={12}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body className="p-4">
 <SectionHeader
 title="Top Stock Items"
 subtitle="Highest on-hand stock items across the inventory."
 action={
 <Button as={Link} to="/inventory-stock-ledger" variant="outline-primary" size="sm">
 Open Ledger
 </Button>
 }
 />
 {topItemsData.length ? (
 <ResponsiveContainer width="100%" height={300}>
 <PieChart>
 <Pie
 data={topItemsData}
 dataKey="qty"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={108}
 innerRadius={58}
 paddingAngle={3}
 label
 >
 {topItemsData.map((entry, index) => (
 <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 ) : (
 <div className="text-center text-muted py-5">No stock data available</div>
 )}
 </Card.Body>
 </Card>
 </Col>

 <Col xl={4} lg={12}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body className="p-4">
 <SectionHeader
 title="Low Stock Alerts"
 subtitle="Items that need attention first."
 />
 {lowStockItems.length ? (
 <div className="table-responsive">
 <Table hover className="align-middle mb-0">
 <thead>
 <tr>
 <th>Item</th>
 <th className="text-center">Warehouse</th>
 <th className="text-center">On Hand</th>
 </tr>
 </thead>
 <tbody>
 {lowStockItems.map((row) => (
 <tr key={row._id}>
 <td>{row.itemName}</td>
 <td className="text-center">{row.warehouse?.warehouseName || "-"}</td>
 <td className="text-center">
 <Badge bg={Number(row.quantityOnHand || 0) <= 0 ? "danger" : "warning"}>
 {Number(row.quantityOnHand || 0)}
 </Badge>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 ) : (
 <div className="text-center text-muted py-5">No low stock items</div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default InventoryDashboard;
